import React, { useRef, useState } from 'react';
import { FileText, Upload, Trash2, Loader2, File, Image } from 'lucide-react';
import toast from 'react-hot-toast';
import { addFileToFacility, removeFileFromFacility } from '../../../../../../services/staffingService';

const CATEGORY_LABELS = {
  document:    'Document',
  image:       'Image',
  layout_plan: 'Layout Plan',
  other:       'Other',
};

function FileIcon({ fileType }) {
  if (fileType?.startsWith('image/')) return <Image size={16} className="text-blue-500" />;
  return <File size={16} className="text-gray-400" />;
}

/**
 * DocumentsTab — purely presentational.
 * Props: facility, customerCompanyId, onFileAdd(newFile), onFileRemove(fileId)
 */
const DocumentsTab = ({ facility, customerCompanyId, onFileAdd, onFileRemove }) => {
  const fileInputRef = useRef(null);
  const [uploading,  setUploading]  = useState(false);
  const [removing,   setRemoving]   = useState(null); // fileId being removed
  const [category,   setCategory]   = useState('document');

  const files = facility.files || [];

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fileCategory', category);
      const resp = await addFileToFacility(customerCompanyId, facility._id, formData);
      onFileAdd(resp.data);
      toast.success('File uploaded');
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleRemove = async (fileId) => {
    setRemoving(fileId);
    try {
      await removeFileFromFacility(customerCompanyId, facility._id, fileId);
      onFileRemove(fileId);
      toast.success('File removed');
    } catch {
      toast.error('Failed to remove file');
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div className="space-y-5 p-6">
      {/* Upload area */}
      <div className="rounded-xl border border-dashed border-border bg-muted/30 p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex-1">
            <p className="text-sm font-medium text-foreground mb-1">Upload a document</p>
            <p className="text-xs text-muted-foreground">PDF, images, layout plans — max 10 MB</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <select
              value={category}
              onChange={e => setCategory(e.target.value)}
              className="rounded-lg border border-border px-2 py-1.5 text-xs focus:outline-none focus:border-[#404880] bg-background"
            >
              {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-1.5 rounded-lg bg-[#404880] px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx"
              onChange={handleFileChange}
            />
          </div>
        </div>
      </div>

      {/* File list */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="border-b border-border px-5 py-3">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <FileText size={14} className="text-[#404880]" />
            Files
            {files.length > 0 && (
              <span className="rounded-full bg-[#404880]/10 px-2 py-0.5 text-[10px] font-semibold text-[#404880]">
                {files.length}
              </span>
            )}
          </h3>
        </div>

        {files.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <FileText size={28} className="mb-2 text-muted-foreground opacity-30" />
            <p className="text-sm text-muted-foreground">No files uploaded yet</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {files.map(f => (
              <div key={f._id} className="flex items-center gap-3 px-5 py-3.5">
                <FileIcon fileType={f.fileType} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{f.filename || 'File'}</p>
                  <p className="text-xs text-muted-foreground">
                    {CATEGORY_LABELS[f.fileCategory] || 'Document'}
                    {f.uploadedAt && <> · {new Date(f.uploadedAt).toLocaleDateString()}</>}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {f.url && (
                    <a href={f.url} target="_blank" rel="noreferrer"
                      className="text-xs text-[#404880] hover:underline">View</a>
                  )}
                  <button
                    onClick={() => handleRemove(f._id)}
                    disabled={removing === f._id}
                    className="p-1 text-muted-foreground hover:text-red-500 transition-colors disabled:opacity-40"
                  >
                    {removing === f._id
                      ? <Loader2 size={14} className="animate-spin" />
                      : <Trash2 size={14} />
                    }
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentsTab;
